// TAPINTO® Editor Frontend - Auth Store
// Version: 1.0
// Date: 2025-10-01

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { AuthState, User, LoginRequest } from '../types';
import { apiService } from '../services/api';
import toast from 'react-hot-toast';

interface AuthStore {
  user: User | null;
  accessToken: string | null;
  refreshTokenValue: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginRequest) => Promise<boolean>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<boolean>;
  setUser: (user: User) => void;
  clearAuth: () => void;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshTokenValue: null,
      isAuthenticated: false,
      isLoading: false,

      login: async (credentials: LoginRequest) => {
        set({ isLoading: true });
        
        try {
          const response = await apiService.login(credentials);
          
          if (response.success && response.data) {
            const { user, access_token, refresh_token } = response.data;
            
            set({
              user,
              accessToken: access_token,
              refreshTokenValue: refresh_token,
              isAuthenticated: true,
              isLoading: false,
            });
            
            toast.success(`Welcome back, ${user.email}!`);
            return true;
          } else {
            toast.error(response.error || 'Login failed');
            set({ isLoading: false });
            return false;
          }
        } catch (error) {
          console.error('Login error:', error);
          toast.error('Login failed. Please try again.');
          set({ isLoading: false });
          return false;
        }
      },

      logout: async () => {
        try {
          await apiService.logout();
        } catch (error) {
          console.error('Logout error:', error);
        } finally {
          set({
            user: null,
            accessToken: null,
            refreshTokenValue: null,
            isAuthenticated: false,
            isLoading: false,
          });
          toast.success('Logged out successfully');
        }
      },

      refreshToken: async () => {
        const { refreshTokenValue } = get();
        if (!refreshTokenValue) return false;

        try {
          const response = await apiService.getPermissions();
          
          if (response.success) {
            return true;
          } else {
            get().clearAuth();
            return false;
          }
        } catch (error) {
          console.error('Token refresh error:', error);
          get().clearAuth();
          return false;
        }
      },

      setUser: (user: User) => {
        set({ user });
      },

      clearAuth: () => {
        set({
          user: null,
          accessToken: null,
          refreshTokenValue: null,
          isAuthenticated: false,
          isLoading: false,
        });
      },
    }),
    {
      name: 'tapinto-auth',
      partialize: (state) => ({
        user: state.user,
        // Do not persist isAuthenticated; tokens are in-memory only
      }),
    }
  )
);
